import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import Ticker from "./Ticker";
import { COMPANY, NAV_LINKS } from "@/constants/company";

export default function Header() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const linkClass = ({ isActive }) =>
    `nav-underline text-sm font-medium transition-colors duration-200 hover:text-brand ${
      isActive ? "text-brand" : "text-slate-300"
    }`;

  const mobileLinkClass = ({ isActive }) =>
    `block border-b border-navy-600/40 py-4 text-base font-medium transition-colors duration-200 ${
      isActive ? "text-brand" : "text-slate-200"
    }`;

  return (
    <header className="fixed inset-x-0 top-0 z-50" data-testid="site-header">
      <Ticker />
      <nav className="border-b border-navy-600/60 bg-navy/90 backdrop-blur-md">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-6 md:px-12">
          <Link to="/" className="flex items-center gap-3" data-testid="nav-logo-link" onClick={() => setOpen(false)}>
            <img
              src={COMPANY.logo}
              alt={`${COMPANY.name} logo`}
              className="h-10 w-10 rounded-full object-cover ring-1 ring-navy-600"
            />
            <span className="leading-tight">
              <span className="block font-heading text-base font-bold tracking-wide text-white">
                EXCELLENT TRADERS
              </span>
              <span className="block font-mono text-[10px] uppercase tracking-[0.3em] text-brand">
                Pte Ltd · Singapore
              </span>
            </span>
          </Link>

          <div className="hidden items-center gap-8 lg:flex">
            {NAV_LINKS.map((l) => (
              <NavLink key={l.to} to={l.to} className={linkClass} data-testid={`nav-link-${l.label.toLowerCase()}`}>
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              data-testid="nav-cta-quote"
              className="rounded-md bg-brand px-5 py-2 text-sm font-semibold text-navy transition-transform duration-200 hover:scale-105 hover:shadow-[0_0_24px_rgba(0,208,132,0.35)]"
            >
              Request Quote
            </Link>
          </div>

          <button
            className="flex h-11 w-11 items-center justify-center rounded-md text-white transition-colors hover:bg-navy-700 lg:hidden"
            onClick={() => setOpen(!open)}
            aria-label="Toggle navigation menu"
            aria-expanded={open}
            data-testid="mobile-menu-toggle"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
              className="overflow-hidden border-t border-navy-600/60 bg-navy-800 lg:hidden"
              data-testid="mobile-menu"
            >
              <div className="flex max-h-[calc(100vh-6rem)] flex-col overflow-y-auto px-6 py-4">
                {NAV_LINKS.map((l, i) => (
                  <motion.div
                    key={l.to}
                    initial={{ opacity: 0, x: -16 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.08 + i * 0.06, duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                  >
                    <NavLink
                      to={l.to}
                      onClick={() => setOpen(false)}
                      className={mobileLinkClass}
                      data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                    >
                      {l.label}
                    </NavLink>
                  </motion.div>
                ))}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.4, duration: 0.3 }}
                  className="pb-2 pt-5"
                >
                  <Link
                    to="/contact"
                    onClick={() => setOpen(false)}
                    data-testid="mobile-nav-cta-quote"
                    className="block rounded-md bg-brand px-5 py-3.5 text-center text-sm font-semibold text-navy"
                  >
                    Request Quote
                  </Link>
                  <a
                    href={`mailto:${COMPANY.email}`}
                    className="mt-4 block text-sm text-brand"
                    data-testid="mobile-nav-email"
                  >
                    {COMPANY.email}
                  </a>
                  <p className="mt-1.5 text-xs leading-relaxed text-slate-500">{COMPANY.address}</p>
                </motion.div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </header>
  );
}
